import * as z from "zod";

export const ANALYTICS_QUERY_VERSION = 2 as const;

export type AnalyticsQueryVersion = typeof ANALYTICS_QUERY_VERSION;

/** Every list and string on the wire is bounded; the numbers live here and nowhere else. */
export const ANALYTICS_QUERY_LIMITS = {
  definitionsMax: 64,
  columnsMax: 45,
  filtersMax: 20,
  sortsMax: 45,
  argsMax: 8,
  casesMax: 16,
  keysMax: 8,
  amongMax: 5,
  sequenceMax: 5,
  nameMax: 120,
  labelMax: 120,
  textMax: 1000,
  stepsMax: 10_000,
  rowsMax: 100_000,
  tilesMax: 1000,
} as const;

export const AnalyticsNameKind = {
  Catalog: "catalog",
  Local: "local",
  Lifted: "lifted",
} as const;

export type AnalyticsNameKind =
  (typeof AnalyticsNameKind)[keyof typeof AnalyticsNameKind];

export const AnalyticsSensitivity = {
  Public: "public",
  Internal: "internal",
  Sensitive: "sensitive",
  Pii: "pii",
} as const;

export type AnalyticsSensitivity =
  (typeof AnalyticsSensitivity)[keyof typeof AnalyticsSensitivity];

export const AnalyticsNodeKind = {
  Ref: "ref",
  Constant: "constant",
  Call: "call",
  Categorize: "categorize",
  Aggregate: "aggregate",
  Relative: "relative",
} as const;

export type AnalyticsNodeKind =
  (typeof AnalyticsNodeKind)[keyof typeof AnalyticsNodeKind];

/** `all`: this computation ignores the query's restrictions. */
export const AnalyticsScope = { All: "all" } as const;

export type AnalyticsScope =
  (typeof AnalyticsScope)[keyof typeof AnalyticsScope];

export const AnalyticsScopeBy = { Team: "team", Account: "account" } as const;

export type AnalyticsScopeBy =
  (typeof AnalyticsScopeBy)[keyof typeof AnalyticsScopeBy];

export const AnalyticsTimeUnit = {
  Hour: "hour",
  Day: "day",
  Week: "week",
  Month: "month",
  Quarter: "quarter",
  Year: "year",
} as const;

export type AnalyticsTimeUnit =
  (typeof AnalyticsTimeUnit)[keyof typeof AnalyticsTimeUnit];

export const AnalyticsTimeUnitSchema = z.enum(AnalyticsTimeUnit);

/** Units that identify a calendar day or coarser: the result of a bucket is a date. */
export const ANALYTICS_DAY_OR_COARSER_UNITS = [
  AnalyticsTimeUnit.Day,
  AnalyticsTimeUnit.Week,
  AnalyticsTimeUnit.Month,
  AnalyticsTimeUnit.Quarter,
  AnalyticsTimeUnit.Year,
] as const satisfies readonly AnalyticsTimeUnit[];

export type AnalyticsDayOrCoarserUnit =
  (typeof ANALYTICS_DAY_OR_COARSER_UNITS)[number];

export const AnalyticsDayOrCoarserUnitSchema = z.enum(
  ANALYTICS_DAY_OR_COARSER_UNITS,
);

export const AnalyticsDirection = {
  Ascending: "ascending",
  Descending: "descending",
} as const;

export type AnalyticsDirection =
  (typeof AnalyticsDirection)[keyof typeof AnalyticsDirection];

export const AnalyticsDirectionSchema = z.enum(AnalyticsDirection);

/** `distinct`: 1,2,3 (row_number). `shared`: 1,1,3 (rank). `packed`: 1,1,2 (dense_rank). */
export const AnalyticsTiePolicy = {
  Distinct: "distinct",
  Shared: "shared",
  Packed: "packed",
} as const;

export type AnalyticsTiePolicy =
  (typeof AnalyticsTiePolicy)[keyof typeof AnalyticsTiePolicy];

export const AnalyticsTiePolicySchema = z.enum(AnalyticsTiePolicy);

/** Fraction of the group at or before this row: by rank (percent_rank) or cumulative (cume_dist). */
export const AnalyticsPositionFraction = {
  Rank: "rank",
  Cumulative: "cumulative",
} as const;

export type AnalyticsPositionFraction =
  (typeof AnalyticsPositionFraction)[keyof typeof AnalyticsPositionFraction];

export const AnalyticsPositionFractionSchema = z.enum(
  AnalyticsPositionFraction,
);

export const AnalyticsRelativeOp = {
  Position: "position",
  PositionFraction: "position_fraction",
  Tile: "tile",
  Neighbor: "neighbor",
  Running: "running",
} as const;

export type AnalyticsRelativeOp =
  (typeof AnalyticsRelativeOp)[keyof typeof AnalyticsRelativeOp];

export const AnalyticsRelativeOpSchema = z.enum(AnalyticsRelativeOp);

/** Which row a `neighbor` reads: a step away, an end or index of the group, or a sequence distance back or ahead. */
export const AnalyticsNeighborKind = {
  Previous: "previous",
  Next: "next",
  First: "first",
  Last: "last",
  Nth: "nth",
  Back: "back",
  Ahead: "ahead",
} as const;

export type AnalyticsNeighborKind =
  (typeof AnalyticsNeighborKind)[keyof typeof AnalyticsNeighborKind];

/**
 * How far a `running` span reaches on one side of the current row. A `_short`
 * extent stops that far short of the current row, so the span leaves it out.
 */
export const AnalyticsSpanExtentKind = {
  None: "none",
  All: "all",
  Rows: "rows",
  RowsShort: "rows_short",
  Time: "time",
  TimeShort: "time_short",
  Value: "value",
  ValueShort: "value_short",
} as const;

export type AnalyticsSpanExtentKind =
  (typeof AnalyticsSpanExtentKind)[keyof typeof AnalyticsSpanExtentKind];

/** What a `running` span counts on both sides: rows, or time or a plain number measured on the sequence's value. */
export const AnalyticsSpanUnit = {
  Rows: "rows",
  Time: "time",
  Value: "value",
} as const;

export type AnalyticsSpanUnit =
  (typeof AnalyticsSpanUnit)[keyof typeof AnalyticsSpanUnit];

/** Semantic type constants: what a value means. */
export const AnalyticsSemanticType = {
  Id: "id",
  Text: "text",
  Category: "category",
  Boolean: "boolean",
  Date: "date",
  Datetime: "datetime",
  Money: "money",
  Quantity: "quantity",
  Ratio: "ratio",
  Duration: "duration",
  List: "list",
} as const;

export type AnalyticsSemanticType =
  (typeof AnalyticsSemanticType)[keyof typeof AnalyticsSemanticType];

/** Aggregate operators and their result semantics. */
export const AnalyticsAggregateOp = {
  Sum: "sum",
  Average: "average",
  Minimum: "minimum",
  Maximum: "maximum",
  Median: "median",
  Percentile: "percentile",
  DiscretePercentile: "discrete_percentile",
  Spread: "spread",
  Variance: "variance",
  Count: "count",
  CountDistinct: "count_distinct",
  Every: "every",
  Some: "some",
  List: "list",
  Single: "single",
  Earliest: "earliest",
  Latest: "latest",
} as const;

export type AnalyticsAggregateOp =
  (typeof AnalyticsAggregateOp)[keyof typeof AnalyticsAggregateOp];

/** Arithmetic operators on semantic types. */
export const AnalyticsArithmeticOp = {
  Add: "add",
  Subtract: "subtract",
  Multiply: "multiply",
  Divide: "divide",
} as const;

export type AnalyticsArithmeticOp =
  (typeof AnalyticsArithmeticOp)[keyof typeof AnalyticsArithmeticOp];
